import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useContext,
} from 'react';
import { IAppState } from 'spw-redux/reducer';
import { connect, useDispatch } from 'react-redux';
import {
  IBuilding,
  IProject,
  IProjectColors,
  IProjectSpaceTypeCategory,
  UserRoleInProject,
} from 'spw-redux/modules/projects/types';
import { Box, Grid } from '@mui/material';
import { KeyDataPoints } from 'spw-programming-projects/program-summary/single-page-summary/key-data-points';
import { SummaryContainer } from 'spw-programming-projects/program-summary/components/styles';
import {
  isMacro,
  getProjectSpaceTypeCategoriesMap,
} from 'spw-components/worksheet/utils';
import TotalProgramAllocationUsfChart from 'spw-components/charts/program-summary/programming/total-program-allocation-usf-chart';
import { pathOr } from 'ramda';
import SpaceTypeAllocationUsfDetailChart from 'spw-components/charts/program-summary/programming/space-type-allocation-usf-detail-chart';
import { HeadcountAnalysis } from 'spw-programming-projects/program-summary/single-page-summary/headcount-analysis';
import { SeatCountAnalysis } from 'spw-programming-projects/program-summary/single-page-summary/seat-count-analysis';
import { HeadcountUsf } from 'spw-programming-projects/program-summary/single-page-summary/headcount-usf';
import { SeatCountUsf } from 'spw-programming-projects/program-summary/single-page-summary/seatcount-usf';
import { UsfBreakdown } from 'spw-programming-projects/program-summary/single-page-summary/usf-breakdown';
import { ProjectOverview } from 'spw-programming-projects/program-summary/single-page-summary/project-overview';
import { MeetingSeatRatio } from 'spw-programming-projects/program-summary/single-page-summary/meeting-seat-ratio';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { valueOrZero } from 'spw-shared/utils';
import { IProjectSettings } from 'spw-redux/modules/settings/types';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_SPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import {
  selectCurrentScenario,
  selectProgramSummaryCalculator,
} from 'spw-redux/modules/calculators/selectors';
import { ScrollableGrid } from 'spw-shared/globalComponents';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { selectProjectSpaceTypesAsMap } from 'spw-redux/modules/space_types/selectors';
import { colors } from 'spw-theme';
import { toggleClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/actions';
import TopNavigation from 'spw-programming-projects/program-summary/top-navigation';
import { ProgramSummaryContext } from 'spw-programming-projects/program-summary/program-summary-context';
import { ProgrammingSummaryCardsContext } from 'spw-programming-projects/program-summary/program-summary-tabs/summary-card-context';
import ImageSelectorPopover from '../../../../components/project-details-tabs/program-summary/shared/image-selector-popover';
import { PaddedGrid } from '../macro-single-page-summary/styles';
import { CategorySummaryRow } from './CategorySummaryRow';
import { CategoryUsfMix } from '../cards/category-usf-mix';
import { CategorySeatTypeMix } from '../cards/category-seat-type-mix';
import { PlainWorksheet } from '../../../../models/Worksheet';
import DraggableNavigationMenu from '../../../../components/draggable-navigation-menu';
import { useNavigation } from '../../../../components/draggable-navigation-menu/use-navigation';
import { SummaryCalculator } from '../../../../models/ProgramSummaryCalculator';
import { PlainProgramScenario } from '../../../../models/Scenario';

interface ISinglePageSummary {
  project: IProject;
  scenario: PlainProgramScenario;
  worksheet: PlainWorksheet;
  totalProgramWorksheets?: PlainWorksheet[];
  summaryCalculator: SummaryCalculator;
  building: IBuilding;
  shouldShowChildrenRowsForDownload?: boolean;
  shouldShowRsfForDownload?: boolean;
  clientFacingNamesAreVisible?: boolean;
  projectColors: IProjectColors;
  projectSettings: IProjectSettings;
  isRenderingForExport?: boolean;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
}

export const SinglePageSummary = ({
  project,
  worksheet,
  totalProgramWorksheets,
  summaryCalculator,
  scenario,
  building,
  shouldShowChildrenRowsForDownload = true,
  shouldShowRsfForDownload,
  clientFacingNamesAreVisible,
  projectColors,
  projectSettings,
  isRenderingForExport,
  projectSpaceTypes,
}: ISinglePageSummary) => {
  const dispatch = useDispatch();
  const isTotalProgramScenario = isTotalProgram(scenario);
  const isExpandedScenario = !scenario?.isShared;
  const isDetailedScenario = scenario?.isDetailed;

  const [open, setOpen] = useState(false);
  const [
    imageSelectorAnchor,
    setImageSelectorAnchor,
  ] = useState<Element | null>(null);

  const { programSummaryRef, refMap } = useNavigation(
    project?.spaceTypeCategories
  );

  const keyDataPointsCardRef = useRef<HTMLElement>(null);
  const headcountAnalysisCardRef = useRef<HTMLElement>(null);
  const seatCountAnalysisCardRef = useRef<HTMLElement>(null);
  const headcountUsfCardRef = useRef<HTMLElement>(null);
  const seatCountUsfCardRef = useRef<HTMLElement>(null);
  const spaceTypeAllocationUsfDetailChartCardRef = useRef<HTMLElement>(null);
  const usfBreakdownCardRef = useRef<HTMLElement>(null);
  const meetingSeatRatioCardRef = useRef<HTMLElement>(null);

  const futureWorkSeats = scenario?.workSeats;
  const futureHeadcount = scenario?.headcountWithUtilization;

  const totalAvailableUsf = valueOrZero(scenario?.totalAvailableUSF);

  const totalProgramRsf = valueOrZero(summaryCalculator?.totalProgramRSF);

  const totalProgramUsf = valueOrZero(summaryCalculator?.totalProgramUSF);

  const rentableLoadFactor = valueOrZero(summaryCalculator?.rentableLoadFactor);

  const rsfLoadFactorPercentage = valueOrZero(
    summaryCalculator?.rsfLoadFactorPercentage
  );

  const { showRsf, setShowRsf } = useContext(ProgrammingSummaryCardsContext);
  const projectSpaceTypeCategories = project?.spaceTypeCategories;
  const rsfExists = scenario?.isRsfLoadFactorSetByUser;

  useEffect(() => {
    setShowRsf(
      shouldShowRsfForDownload === undefined
        ? rsfExists
        : shouldShowRsfForDownload
    );
  }, [setShowRsf, shouldShowRsfForDownload, rsfExists]);

  useEffect(() => {
    setOpen(isDetailedScenario ? shouldShowChildrenRowsForDownload : false);
  }, [isDetailedScenario, shouldShowChildrenRowsForDownload]);

  const {
    individualWorkspaces: individualWorkspacesCategory,
    meetingSpaces: meetingSpacesCategory,
    amenitySpaces: amenitySpacesCategory,
    supportSpaces: supportSpacesCategory,
  } = useMemo(() => {
    return getProjectSpaceTypeCategoriesMap(projectSpaceTypeCategories);
  }, [projectSpaceTypeCategories]);

  const individualWorkspacesTotals =
    worksheet?.categoriesTotals[INDIVIDUAL_SPACES_CATEGORY];
  const meetingSpacesTotals =
    worksheet?.categoriesTotals[MEETING_SPACES_CATEGORY];
  const amenitySpacesTotals =
    worksheet?.categoriesTotals[AMENITY_SPACES_CATEGORY];
  const supportSpacesTotals =
    worksheet?.categoriesTotals[SUPPORT_SPACES_CATEGORY];

  const sharedSpacesUsf =
    (meetingSpacesTotals?.USF || 0) +
    (amenitySpacesTotals?.USF || 0) +
    (supportSpacesTotals?.USF || 0);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const viewOnly = project?.currentUserRole === UserRoleInProject.viewer;

  const { visibleCards } = useContext(ProgramSummaryContext);

  if (!worksheet || !scenario || !project || !summaryCalculator) return null;

  return (
    <div
      style={{
        backgroundColor: isRenderingForExport
          ? colors.lightGreyBackground
          : 'white',
      }}
      ref={programSummaryRef}
    >
      {!isRenderingForExport && !viewOnly && (
        <>
          <ImageSelectorPopover
            project={project}
            anchorEl={imageSelectorAnchor}
            onClose={() => setImageSelectorAnchor(null)}
          />
          <DraggableNavigationMenu
            refMap={refMap}
            parent={programSummaryRef}
            getNavigationButtonProps={(spaceTypeCategory, scrollTo) => {
              const { category } = spaceTypeCategory.defaultSpaceTypeCategory;

              return {
                className:
                  worksheet && !worksheet.rowsByCategory[category]?.length
                    ? 'disabled'
                    : '',
                onClick: () => {
                  if (worksheet) {
                    if (worksheet.rowsByCategory[category]?.length) {
                      scrollTo(spaceTypeCategory.id);
                    }
                  } else {
                    scrollTo(spaceTypeCategory.id);
                  }
                },
              };
            }}
          />
        </>
      )}
      {!isRenderingForExport && (
        <TopNavigation
          clientFacingNamesAreVisible={clientFacingNamesAreVisible}
          onToggleClientFacingNames={() =>
            dispatch(toggleClientFacingNamesAreVisible())
          }
          showRsf={showRsf}
          onToggleRsf={() => setShowRsf(!showRsf)}
        />
      )}

      <SummaryContainer
        id="program-summary-container"
        style={{ position: 'relative', padding: isRenderingForExport ? 0 : 16 }}
      >
        <Grid container>
          {(!isRenderingForExport ||
            visibleCards?.programSummaryReport ||
            visibleCards?.keyDataPoints ||
            visibleCards?.totalProgramAllocation ||
            visibleCards?.spaceTypeAllocation) && (
            <Grid item xs={6} container direction="column">
              {(!isRenderingForExport ||
                visibleCards?.programSummaryReport ||
                visibleCards?.keyDataPoints) && (
                <Grid container>
                  <PaddedGrid item xs={12}>
                    <KeyDataPoints
                      showRsf={showRsf}
                      ref={keyDataPointsCardRef}
                      summaryCalculator={summaryCalculator}
                      scenario={scenario}
                      building={building}
                      futureWorkSeats={futureWorkSeats}
                      futureHeadcount={scenario.headcountWithUtilization}
                      futureMeetingSeats={meetingSpacesTotals?.seats}
                      projectSettings={projectSettings}
                      totalProgramWorksheets={totalProgramWorksheets}
                    />
                  </PaddedGrid>
                </Grid>
              )}

              <Grid
                container
                style={{
                  flex: 1,
                }}
              >
                <Grid item xs={12} container>
                  <Grid container>
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.totalProgramAllocation) && (
                      <PaddedGrid item xs={6}>
                        <TotalProgramAllocationUsfChart
                          summaryCalculator={summaryCalculator}
                          height="100%"
                          detailedSharedConfig={isExpandedScenario}
                          worksheet={worksheet}
                          isTotalProgramScenario={isTotalProgramScenario}
                          individualWorkspacesCategory={
                            individualWorkspacesCategory
                          }
                          individualWorkspacesUsf={
                            individualWorkspacesTotals?.USF
                          }
                          sharedSpacesUsf={sharedSpacesUsf}
                          projectColors={projectColors}
                          totalProgramWorksheets={totalProgramWorksheets}
                        />
                      </PaddedGrid>
                    )}
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.spaceTypeAllocation) && (
                      <PaddedGrid item xs={6}>
                        <SpaceTypeAllocationUsfDetailChart
                          ref={spaceTypeAllocationUsfDetailChartCardRef}
                          worksheet={worksheet}
                          isTotalProgramScenario={isTotalProgramScenario}
                          projectSpaceTypeCategories={
                            project?.spaceTypeCategories
                          }
                          spaceTypesUsf={{
                            individualWorkspaces:
                              individualWorkspacesTotals?.USF,
                            meetingSpaces: meetingSpacesTotals?.USF,
                            amenitySpaces: amenitySpacesTotals?.USF,
                            supportSpaces: supportSpacesTotals?.USF,
                          }}
                          showNamesInTable={false}
                          totalProgramWorksheets={totalProgramWorksheets}
                        />
                      </PaddedGrid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          <Grid item xs={6}>
            <Grid container style={{ height: '100%' }}>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  flexDirection="column"
                  style={{ height: '100%' }}
                >
                  <Grid container>
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.headcountAnalysis) && (
                      <PaddedGrid item xs={6}>
                        <HeadcountAnalysis
                          ref={headcountAnalysisCardRef}
                          futureHeadcount={futureHeadcount}
                          scenario={scenario}
                          totalProgramWorksheets={
                            isTotalProgramScenario
                              ? totalProgramWorksheets
                              : null
                          }
                        />
                      </PaddedGrid>
                    )}

                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.seatCountAnalysis) && (
                      <PaddedGrid item xs={6}>
                        <SeatCountAnalysis
                          ref={seatCountAnalysisCardRef}
                          futureWorkSeats={futureWorkSeats}
                          futureHeadcount={futureHeadcount}
                          deskSharingRatio={scenario.deskSharingRatio}
                          isTotalProgramScenario={isTotalProgramScenario}
                          totalProgramWorksheets={
                            isTotalProgramScenario
                              ? totalProgramWorksheets
                              : null
                          }
                        />
                      </PaddedGrid>
                    )}
                  </Grid>

                  <Grid container>
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.headcountUsf) && (
                      <PaddedGrid item xs={6}>
                        <HeadcountUsf
                          showRsf={showRsf}
                          ref={headcountUsfCardRef}
                          isTotalProgramScenario={isTotalProgramScenario}
                          totalAvailableUsf={totalAvailableUsf}
                          totalProgramRsf={totalProgramRsf}
                          totalProgramUsf={totalProgramUsf}
                          futureHeadcount={futureHeadcount}
                          totalProgramWorksheets={
                            isTotalProgramScenario
                              ? totalProgramWorksheets
                              : null
                          }
                        />
                      </PaddedGrid>
                    )}
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.seatCountUsf) && (
                      <PaddedGrid item xs={6}>
                        <SeatCountUsf
                          showRsf={showRsf}
                          ref={seatCountUsfCardRef}
                          totalAvailableUsf={totalAvailableUsf}
                          totalProgramRsf={totalProgramRsf}
                          totalProgramUsf={totalProgramUsf}
                          futureWorkSeats={futureWorkSeats}
                          isTotalProgramScenario={isTotalProgramScenario}
                          totalProgramWorksheets={
                            isTotalProgramScenario
                              ? totalProgramWorksheets
                              : null
                          }
                        />
                      </PaddedGrid>
                    )}
                  </Grid>

                  <Grid container style={{ flexGrow: 1 }}>
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.squareFootageBreakdown) && (
                      <PaddedGrid item xs={6}>
                        <UsfBreakdown
                          showRsf={showRsf}
                          ref={usfBreakdownCardRef}
                          height="100%"
                          planningFactorPercent={
                            summaryCalculator.planningFactorPercentage
                          }
                          planningFactor={
                            summaryCalculator.additionalUSFForPlanningFactor
                          }
                          circulationPercent={
                            summaryCalculator.circulationPercentage
                          }
                          circulation={summaryCalculator.circulationUSF}
                          subTotalProgramUsf={worksheet.subtotalProgramUSF}
                          totalProgramRSF={totalProgramRsf}
                          totalProgramUsf={totalProgramUsf}
                          isTotalProgramScenario={isTotalProgramScenario}
                          totalProgramWorksheets={
                            isTotalProgramScenario
                              ? totalProgramWorksheets
                              : null
                          }
                          rentableLoadFactor={rentableLoadFactor}
                          rsfLoadFactorPercentage={rsfLoadFactorPercentage}
                        />
                      </PaddedGrid>
                    )}
                    {(!isRenderingForExport ||
                      visibleCards?.programSummaryReport ||
                      visibleCards?.meetingSeatRatio) && (
                      <PaddedGrid item xs={6}>
                        <MeetingSeatRatio
                          ref={meetingSeatRatioCardRef}
                          height="100%"
                          futureWorkSeats={futureWorkSeats}
                          futureMeetingSeats={meetingSpacesTotals?.seats}
                          isTotalProgramScenario={isTotalProgramScenario}
                          totalProgramWorksheets={
                            isTotalProgramScenario
                              ? totalProgramWorksheets
                              : null
                          }
                        />
                      </PaddedGrid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <ScrollableGrid
            container
            ref={refMap[individualWorkspacesCategory.id]}
            $scrollMarginTop={110}
          >
            {(!isRenderingForExport ||
              visibleCards?.categoryUsfAndSeatMix?.[INDIVIDUAL_SPACES_CATEGORY]
                ?.usfAndSeatMix ||
              visibleCards?.categoryUsfAndSeatMix?.[INDIVIDUAL_SPACES_CATEGORY]
                ?.usfMix) && (
              <PaddedGrid item xs={6}>
                <CategoryUsfMix
                  viewOnly={viewOnly}
                  projectSpaceTypes={projectSpaceTypes}
                  onClickSpaceIcon={(e) => {
                    setImageSelectorAnchor(e);
                  }}
                  projectSpaceTypeCategory={individualWorkspacesCategory}
                  worksheet={worksheet}
                  open={open}
                  setOpen={toggleOpen}
                  detailedIndividualConfig={isDetailedScenario}
                  isMacro={isMacro(scenario)}
                  projectColors={projectColors}
                  isRenderingForExport={isRenderingForExport}
                  clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                />
              </PaddedGrid>
            )}
            {(!isRenderingForExport ||
              visibleCards?.categoryUsfAndSeatMix?.[INDIVIDUAL_SPACES_CATEGORY]
                ?.usfAndSeatMix ||
              visibleCards?.categoryUsfAndSeatMix?.[INDIVIDUAL_SPACES_CATEGORY]
                ?.seatMix) && (
              <PaddedGrid
                item
                xs={
                  visibleCards?.categoryUsfAndSeatMix?.[
                    INDIVIDUAL_SPACES_CATEGORY
                  ]?.usfAndSeatMix &&
                  !visibleCards?.categoryUsfAndSeatMix?.[
                    INDIVIDUAL_SPACES_CATEGORY
                  ]?.seatMix
                    ? 6
                    : 4
                }
              >
                <CategorySeatTypeMix
                  viewOnly={viewOnly}
                  projectSpaceTypes={projectSpaceTypes}
                  onClickSpaceIcon={(e) => {
                    setImageSelectorAnchor(e);
                  }}
                  projectSpaceTypeCategory={individualWorkspacesCategory}
                  worksheet={worksheet}
                  open={open}
                  detailedIndividualConfig={isDetailedScenario}
                  projectColors={projectColors}
                  isRenderingForExport={isRenderingForExport}
                  clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                />
              </PaddedGrid>
            )}
            {(!isRenderingForExport || visibleCards?.projectOverview) && (
              <PaddedGrid item xs={2}>
                <ProjectOverview
                  height="100%"
                  scenario={scenario}
                  building={building}
                  project={project}
                />
              </PaddedGrid>
            )}
          </ScrollableGrid>
          {[
            meetingSpacesCategory,
            amenitySpacesCategory,
            supportSpacesCategory,
          ].map((projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
            const {
              category,
            } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
            return (
              worksheet?.rowsByCategory[category]?.length > 0 && (
                <CategorySummaryRow
                  viewOnly={viewOnly}
                  projectSpaceTypes={projectSpaceTypes}
                  onClickSpaceIcon={(e) => {
                    setImageSelectorAnchor(e);
                  }}
                  reference={refMap[projectSpaceTypeCategory.id]}
                  key={`${projectSpaceTypeCategory.id}`}
                  projectSpaceTypeCategory={projectSpaceTypeCategory}
                  worksheet={worksheet}
                  projectColors={projectColors}
                  shouldShowChildrenRowsForDownload={
                    shouldShowChildrenRowsForDownload
                  }
                  isRenderingForExport={isRenderingForExport}
                  clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                />
              )
            );
          })}
        </Grid>
      </SummaryContainer>
    </div>
  );
};

const mapStateToProps = (state: IAppState) => {
  return {
    project: state.projectsReducer.currentProject,
    scenario: selectCurrentScenario(state),
    worksheet: state.calculatorReducer.worksheet,
    totalProgramWorksheets: state.calculatorReducer.tpsWorksheetsEnhanced,
    summaryCalculator: selectProgramSummaryCalculator(state),
    building: pathOr(
      null,
      ['projectsReducer', 'currentProject', 'buildings', 0],
      state
    ),
    downloadName: state.programSummaryReducer.downloadName,
    projectColors: state.projectsReducer.colors,
    projectSettings: state.settingsReducer.projectSettings,
    projectSpaceTypes: selectProjectSpaceTypesAsMap(state),
  };
};

export default connect(mapStateToProps)(SinglePageSummary);
